import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  BookOpen,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  Code2,
  Copy,
  Download,
  Eye,
  EyeOff,
  FlaskConical,
  Gauge,
  KeyRound,
  Lock,
  LogOut,
  MessageSquare,
  MousePointer2,
  Play,
  Plus,
  RefreshCw,
  Rocket,
  Send,
  ShieldCheck,
  Sparkles,
  TerminalSquare,
  Unlock,
  Users,
  WifiOff,
  X,
  Zap,
} from "lucide-react";
import { BONUS_TASK_COUNT, CORE_TASK_COUNT, CORE_TASK_IDS, finalGameCode, lessons, type CodeTask, type Lesson } from "./data/lessons";
import { ApiError, api, clearSessionToken, setSessionToken, type AdminState, type AppState, type StudentState } from "./lib/api";
import { runPython, type PythonResult } from "./lib/python";
import { registerStudentTools } from "./webmcp";

type LoginRole = "student" | "admin";

function Brand() {
  return (
    <div className="brand" aria-label="CodeQuest">
      <span className="brand-mark"><Code2 size={23} /></span>
      <span><strong>CODE</strong>QUEST</span>
      <small>PYTHON</small>
    </div>
  );
}

function LoginScreen({ onLogin }: { onLogin: () => Promise<void> }) {
  const [role, setRole] = useState<LoginRole>("student");
  const [nickname, setNickname] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [classCode, setClassCode] = useState("CODEQUEST");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await api<{ token: string }>(role === "student" ? "student/login" : "admin/login", {
        method: "POST",
        body: JSON.stringify(role === "student" ? { nickname, accessCode, classCode } : { password, classCode }),
      });
      setSessionToken(result.token);
      await onLogin();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Inloggen lukte niet.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-shell">
      <div className="login-grid" aria-hidden="true" />
      <section className="login-story">
        <Brand />
        <p className="eyebrow"><span /> LIVE PYTHON MISSIE</p>
        <h1>De USB-stick is verdwenen.</h1>
        <p className="login-lead">Vier echte lessen in vijf weken. Eén bonusmissie. Eén speelbare Python escape-room.</p>
        <div className="mission-preview">
          <div className="radar"><span /><Zap size={28} /></div>
          <div>
            <small>EINDDOEL</small>
            <strong>Bouw een speelbare Python escape-room</strong>
            <p>Met kamers, inventaris, beveiligingspuzzels en jouw eigen finale.</p>
          </div>
        </div>
        <div className="mission-stats">
          <span><b>05</b> weken</span>
          <span><b>90</b> min per les</span>
          <span><b>01</b> echte game</span>
        </div>
      </section>

      <section className="login-card" aria-labelledby="login-title">
        <div className="role-switch" role="tablist" aria-label="Kies je rol">
          <button type="button" role="tab" aria-selected={role === "student"} onClick={() => setRole("student")}>Leerling</button>
          <button type="button" role="tab" aria-selected={role === "admin"} onClick={() => setRole("admin")}>Docent</button>
        </div>
        <div className="login-card-head">
          <span className="icon-plate">{role === "student" ? <Rocket /> : <ShieldCheck />}</span>
          <div>
            <p className="eyebrow">MISSION CONTROL</p>
            <h2 id="login-title">{role === "student" ? "Sluit je aan bij de crew" : "Open het beheerpaneel"}</h2>
          </div>
        </div>
        <form onSubmit={submit}>
          {role === "student" ? (
            <>
              <label>
                Jouw codenaam
                <input value={nickname} onChange={(event) => setNickname(event.target.value)} placeholder="bijv. PixelPiraat" autoFocus maxLength={24} autoComplete="username" />
              </label>
              <label>
                Jouw persoonlijke code
                <input type="password" value={accessCode} onChange={(event) => setAccessCode(event.target.value.replace(/[^A-Za-z0-9]/g, ""))} placeholder="4–12 letters of cijfers" minLength={4} maxLength={12} autoComplete="current-password" />
              </label>
            </>
          ) : (
            <label>
              Docentwachtwoord
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••••" autoFocus />
            </label>
          )}
          <label>
            Klascode
            <input className="code-input" value={classCode} onChange={(event) => setClassCode(event.target.value.toUpperCase())} maxLength={20} />
          </label>
          {error && <p className="form-error" role="alert"><X size={16} />{error}</p>}
          <button className="primary-button wide" disabled={busy}>
            {busy ? <RefreshCw className="spin" /> : <KeyRound />}
            {busy ? "Verbinden..." : role === "student" ? "Start missie" : "Naar dashboard"}
          </button>
        </form>
        <p className="privacy-note">Eerste keer? Bedenk zelf een code en onthoud die. Later log je op iedere laptop in met dezelfde klascode, codenaam en persoonlijke code.</p>
      </section>
    </main>
  );
}

function CodeLab({ task, lessonId, state, refresh }: { task: CodeTask; lessonId: number; state: StudentState; refresh: () => Promise<void> }) {
  const draftKey = `codequest:draft:v2:${state.student.id}:${task.id}`;
  const serverDraft = state.drafts.find((draft) => draft.taskId === task.id)?.code;
  const [code, setCode] = useState(() => serverDraft || localStorage.getItem(draftKey) || task.starterCode);
  const [result, setResult] = useState<PythonResult | null>(null);
  const [running, setRunning] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [liveSync, setLiveSync] = useState<"syncing" | "synced" | "error">("syncing");
  const passedBefore = state.progress.find((item) => item.taskId === task.id)?.passed;

  useEffect(() => {
    setCode(serverDraft || localStorage.getItem(draftKey) || task.starterCode);
    setResult(null);
  }, [draftKey, task.starterCode]);

  useEffect(() => {
    setLiveSync("syncing");
    const timer = window.setTimeout(() => {
      void api("live-code", {
        method: "POST",
        body: JSON.stringify({ taskId: task.id, lessonId, code }),
      }).then(() => setLiveSync("synced")).catch(() => setLiveSync("error"));
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [code, lessonId, task.id]);

  async function execute() {
    setRunning(true);
    setResult(null);
    localStorage.setItem(draftKey, code);
    try {
      const next = await runPython(code, task.tests);
      setResult(next);
      const passedTests = next.tests.filter((test) => test.passed).length;
      const score = next.tests.length ? Math.round((passedTests / next.tests.length) * 100) : 0;
      await api("progress", {
        method: "POST",
        body: JSON.stringify({ taskId: task.id, lessonId, passed: next.passed, score }),
      });
      await refresh();
    } catch (cause) {
      setResult({
        passed: false,
        output: "",
        tests: [],
        error: cause instanceof Error ? cause.message : "Python kon niet starten.",
      });
    } finally {
      setRunning(false);
    }
  }

  return (
    <section className={`code-lab ${task.optional ? "bonus-lab" : ""}`} aria-labelledby={`task-${task.id}`}>
      <div className="lab-toolbar">
        <div>
          <span className="window-dots" aria-hidden="true"><i /><i /><i /></span>
          <span className="file-tab">opdracht.py</span>
        </div>
        <div className="lab-toolbar-status">
          <span className={`live-sync ${liveSync}`} title={liveSync === "error" ? "Live delen mislukt. Log opnieuw in in dit tabblad." : "De nieuwste code wordt met de docent gedeeld."}>
            {liveSync === "syncing" ? <RefreshCw className="spin" /> : liveSync === "error" ? <WifiOff /> : <Eye />}
            {liveSync === "syncing" ? "delen…" : liveSync === "error" ? "niet gedeeld" : "live gedeeld"}
          </span>
          <span className={`task-state ${passedBefore ? "passed" : ""} ${task.optional ? "bonus" : ""}`}>
            {passedBefore ? <><Check size={14} /> {task.optional ? "bonus gehaald" : "geslaagd"}</> : task.optional ? "optionele side quest" : "checkpoint"}
          </span>
        </div>
      </div>
      <div className="task-brief">
        <div className={`task-index ${task.optional ? "bonus" : ""}`}>{task.optional ? <Sparkles /> : <TerminalSquare />}{task.optional ? "SIDE QUEST" : "PYTHON"}</div>
        <h3 id={`task-${task.id}`}>{task.title}</h3>
        <p>{task.brief}</p>
      </div>
      <div className="editor-wrap">
        <div className="line-numbers" aria-hidden="true">{code.split("\n").map((_, index) => <span key={index}>{index + 1}</span>)}</div>
        <textarea
          className="code-editor"
          value={code}
          onChange={(event) => setCode(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Tab") {
              event.preventDefault();
              const target = event.currentTarget;
              const start = target.selectionStart;
              const next = `${code.slice(0, start)}    ${code.slice(target.selectionEnd)}`;
              setCode(next);
              requestAnimationFrame(() => target.setSelectionRange(start + 4, start + 4));
            }
          }}
          spellCheck={false}
          maxLength={12000}
          aria-label={`Python-code voor ${task.title}`}
        />
      </div>
      <div className="lab-actions">
        <button className="run-button" onClick={execute} disabled={running}>
          {running ? <RefreshCw className="spin" /> : <Play fill="currentColor" />}
          {running ? "Python opstarten..." : "Run tests"}
        </button>
        <button className="ghost-button" onClick={() => setShowHint((value) => !value)}><CircleHelp /> Hint</button>
        <button className="ghost-button reset-button" onClick={() => { setCode(task.starterCode); setResult(null); localStorage.removeItem(draftKey); }}>Reset</button>
      </div>
      {showHint && <div className="hint-box"><Sparkles size={18} /><span><strong>Hint</strong>{task.hint}</span></div>}
      {result && <TestResults result={result} />}
    </section>
  );
}

function TestResults({ result }: { result: PythonResult }) {
  return (
    <div className={`test-results ${result.passed ? "success" : "needs-work"}`} aria-live="polite">
      <div className="result-head">
        <span>{result.passed ? <Check /> : <FlaskConical />}</span>
        <div>
          <strong>{result.passed ? "Checkpoint gehaald!" : "Bijna—je code draait, maar check dit"}</strong>
          <p>{result.passed ? "Alle tests zijn groen. Je voortgang is opgeslagen." : "Lees per test wat Python kreeg en verwachtte."}</p>
        </div>
      </div>
      {result.error && <pre className="python-error">{result.error}</pre>}
      {result.tests.map((test) => (
        <div className="test-row" key={test.label}>
          <span className={test.passed ? "test-ok" : "test-fail"}>{test.passed ? <Check /> : <X />}</span>
          <div>
            <strong>{test.label}</strong>
            {!test.passed && <small>Python gaf <code>{test.actual}</code> · verwacht <code>{test.expected}</code></small>}
          </div>
        </div>
      ))}
      {result.output && <pre className="python-output">{result.output}</pre>}
    </div>
  );
}

function LessonTimeline({ lesson }: { lesson: Lesson }) {
  let elapsed = 0;
  return (
    <section className="timeline-card">
      <div className="section-title"><Gauge /><div><span>90 MINUTEN</span><h3>Missieplan</h3></div></div>
      <div className="timeline">
        {lesson.timeline.map((item) => {
          const start = elapsed;
          elapsed += item.minutes;
          return (
            <div className={`timeline-row ${item.mode}`} key={item.label}>
              <span>{String(start).padStart(2, "0")}</span>
              <i />
              <div><strong>{item.label}</strong><small>{item.minutes} min</small></div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function QuestionPanel({ state, lessonId, refresh }: { state: StudentState; lessonId: number; refresh: () => Promise<void> }) {
  const [question, setQuestion] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!question.trim()) return;
    setBusy(true);
    setMessage("");
    try {
      await api("questions", { method: "POST", body: JSON.stringify({ lessonId, question }) });
      setQuestion("");
      setMessage("Vraag verzonden naar Mission Control.");
      await refresh();
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Versturen lukte niet.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="question-panel">
      <div className="question-panel-head"><div className="section-title"><MessageSquare /><div><span>HULPLIJN</span><h3>Vraag de docent</h3></div></div><small><i /> LIVE</small></div>
      <form onSubmit={submit}>
        <textarea value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Waar loop je op vast? Vertel ook wat je al probeerde." maxLength={500} />
        <div><small>{question.length}/500</small><button disabled={busy || question.trim().length < 3}><Send /> Verstuur</button></div>
      </form>
      {message && <p className="question-message">{message}</p>}
      <div className="question-history">
        {state.questions.length === 0 ? <p className="empty-note">Nog geen vragen. Eerst zelf debuggen, dan slim om hulp vragen.</p> : state.questions.map((item) => (
          <article key={item.id}>
            <small>MISSIE {item.lessonId} · {item.status === "answered" ? "BEANTWOORD" : "WACHT OP DOCENT"}</small>
            <p>{item.body}</p>
            {item.answer && <blockquote><strong>Mission Control</strong>{item.answer}</blockquote>}
          </article>
        ))}
      </div>
    </section>
  );
}

function useStudentPresence(studentId: string) {
  useEffect(() => {
    const pointerCapable = window.matchMedia("(pointer: fine)").matches;
    let lastActivity = Date.now();
    let lastSent = 0;

    const markActivity = () => { lastActivity = Date.now(); };
    const sendPresence = async (force = false) => {
      const now = Date.now();
      if (!force && now - lastSent < 3_000) return;
      lastSent = now;
      const pointerInside = !pointerCapable || document.documentElement.matches(":hover");
      await api("presence", {
        method: "POST",
        keepalive: true,
        body: JSON.stringify({
          visible: document.visibilityState === "visible",
          focused: document.hasFocus(),
          pointerInside,
          pointerCapable,
          active: now - lastActivity < 60_000,
        }),
      }).catch(() => undefined);
    };
    const sendNow = () => { void sendPresence(true); };

    window.addEventListener("focus", sendNow);
    window.addEventListener("blur", sendNow);
    document.addEventListener("visibilitychange", sendNow);
    window.addEventListener("pointermove", markActivity, { passive: true });
    window.addEventListener("keydown", markActivity);
    window.addEventListener("touchstart", markActivity, { passive: true });
    document.documentElement.addEventListener("pointerenter", sendNow);
    document.documentElement.addEventListener("pointerleave", sendNow);
    const heartbeat = window.setInterval(() => { void sendPresence(true); }, 10_000);
    void sendPresence(true);

    return () => {
      window.clearInterval(heartbeat);
      window.removeEventListener("focus", sendNow);
      window.removeEventListener("blur", sendNow);
      document.removeEventListener("visibilitychange", sendNow);
      window.removeEventListener("pointermove", markActivity);
      window.removeEventListener("keydown", markActivity);
      window.removeEventListener("touchstart", markActivity);
      document.documentElement.removeEventListener("pointerenter", sendNow);
      document.documentElement.removeEventListener("pointerleave", sendNow);
    };
  }, [studentId]);
}

function StudentApp({ state, refresh, logout }: { state: StudentState; refresh: () => Promise<void>; logout: () => Promise<void> }) {
  const initialLesson = Math.min(state.class.openLesson, 5);
  const [selectedLesson, setSelectedLesson] = useState(initialLesson);
  const [selectedTask, setSelectedTask] = useState(0);
  const lesson = lessons[selectedLesson - 1];
  const completed = state.progress.filter((item) => item.passed && CORE_TASK_IDS.has(item.taskId)).length;
  const bonusCompleted = state.progress.filter((item) => item.passed && !CORE_TASK_IDS.has(item.taskId)).length;
  const task = lesson.tasks[selectedTask] ?? lesson.tasks[0];
  useStudentPresence(state.student.id);

  useEffect(() => {
    if (selectedLesson > state.class.openLesson) {
      setSelectedLesson(state.class.openLesson);
      setSelectedTask(0);
    }
  }, [selectedLesson, state.class.openLesson]);

  useEffect(() => {
    const navigate = (event: Event) => {
      const id = Number((event as CustomEvent).detail?.lessonId);
      if (id <= state.class.openLesson) { setSelectedLesson(id); setSelectedTask(0); }
    };
    window.addEventListener("codequest:navigate", navigate);
    return () => window.removeEventListener("codequest:navigate", navigate);
  }, [state.class.openLesson]);

  useEffect(() => registerStudentTools(() => void refresh()), [refresh]);

  function downloadGame() {
    const blob = new Blob([finalGameCode], { type: "text/x-python;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "de-verdwenen-usb-stick.py";
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand />
        <div className="topbar-status"><span className="live-dot" /> MISSIE LIVE</div>
        <div className="student-presence-note" title="De docent ziet je actuele code en activiteitsstatus. Alleen je laatste codeversie wordt gedeeld; er wordt geen codegeschiedenis bijgehouden."><Eye /><span>Status + code zichtbaar</span></div>
        <div className="profile-chip"><span>{state.student.nickname.slice(0, 2).toUpperCase()}</span><div><small>CREW</small><strong>{state.student.nickname}</strong></div></div>
        <button className="icon-button" onClick={logout} aria-label="Uitloggen"><LogOut /></button>
      </header>
      <aside className="mission-nav">
        <div className="progress-ring" style={{ "--progress": `${Math.round((completed / CORE_TASK_COUNT) * 360)}deg` } as React.CSSProperties}>
          <div><strong>{completed}</strong><small>/ {CORE_TASK_COUNT}</small></div>
        </div>
        <p>CHECKPOINTS{bonusCompleted > 0 && <small> +{bonusCompleted} BONUS</small>}</p>
        <nav aria-label="Missies">
          {lessons.map((item) => {
            const locked = item.id > state.class.openLesson;
            const requiredTasks = item.tasks.filter((itemTask) => !itemTask.optional);
            const itemDone = (requiredTasks.length ? requiredTasks : item.tasks).every((itemTask) => state.progress.some((progress) => progress.taskId === itemTask.id && progress.passed));
            return (
              <button
                key={item.id}
                disabled={locked}
                className={`${selectedLesson === item.id ? "active" : ""} ${itemDone ? "done" : ""}`}
                onClick={() => { setSelectedLesson(item.id); setSelectedTask(0); }}
              >
                <span>{locked ? <Lock /> : itemDone ? <Check /> : `0${item.id}`}</span>
                <div><small>{item.code}</small><strong>{item.title}</strong></div>
                {!locked && <ChevronRight />}
              </button>
            );
          })}
        </nav>
        <div className="nav-footer"><BookOpen /><span><small>EINDDOEL</small>Jouw eigen <b>.py</b> game</span></div>
      </aside>

      <main className="mission-main">
        <section className="mission-heading">
          <div>
            <p className="eyebrow"><span /> {lesson.code}</p>
            <h1>{lesson.title}</h1>
            <p>{lesson.payoff}</p>
            <blockquote className="mission-briefing">{lesson.briefing}</blockquote>
          </div>
          <div className="concept-chips">{lesson.concepts.map((concept) => <span key={concept}>#{concept}</span>)}</div>
        </section>

        <div className="student-workspace">
          <div className="workspace-primary">
            <div className="task-tabs" role="tablist" aria-label="Code-opdrachten">
              {lesson.tasks.map((item, index) => {
                const done = state.progress.some((progress) => progress.taskId === item.id && progress.passed);
                const coreIndex = lesson.tasks.slice(0, index + 1).filter((candidate) => !candidate.optional).length;
                return <button role="tab" aria-selected={selectedTask === index} className={`${selectedTask === index ? "active" : ""} ${item.optional ? "bonus" : ""}`} onClick={() => setSelectedTask(index)} key={item.id}><span>{done ? <Check /> : item.optional ? <Sparkles /> : coreIndex}</span>{item.title}</button>;
              })}
            </div>
            <CodeLab key={`${state.student.id}:${task.id}`} task={task} lessonId={lesson.id} state={state} refresh={refresh} />
            {lesson.id === 5 && completed >= CORE_TASK_COUNT && (
              <section className="download-card">
                <div className="download-icon"><Download /></div>
                <div><span>FINAL BUILD</span><h3>Neem je escape-room mee</h3><p>Download de complete game, pas de kamers aan en speel hem met vrienden in Python.</p></div>
                <button onClick={downloadGame}><Download /> Download .py</button>
              </section>
            )}
          </div>
          <aside className="workspace-side">
            <LessonTimeline lesson={lesson} />
            <QuestionPanel state={state} lessonId={lesson.id} refresh={refresh} />
          </aside>
        </div>
      </main>
    </div>
  );
}

function AnswerBox({ question, onAnswered }: { question: AdminState["questions"][number]; onAnswered: () => Promise<void> }) {
  const [answer, setAnswer] = useState(question.answer ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api("admin/answer", { method: "POST", body: JSON.stringify({ questionId: question.id, answer }) });
      await onAnswered();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Antwoorden lukte niet.");
    } finally { setBusy(false); }
  }
  return (
    <article className={`inbox-item ${question.status}`}>
      <div className="inbox-meta"><span>{question.nickname.slice(0, 2).toUpperCase()}</span><div><strong>{question.nickname}</strong><small>MISSIE {question.lessonId}</small></div><time>{new Date(question.createdAt).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}</time></div>
      <p>{question.body}</p>
      <form onSubmit={submit}>
        <textarea value={answer} onChange={(event) => setAnswer(event.target.value)} placeholder="Geef een hint zonder de oplossing weg te geven..." maxLength={1000} />
        <button disabled={busy || answer.trim().length < 2}>{question.status === "answered" ? "Antwoord bijwerken" : "Stuur hint"}<Send /></button>
      </form>
      {error && <small className="form-error">{error}</small>}
    </article>
  );
}

function PresenceBadge({ student }: { student: AdminState["students"][number] }) {
  const states = {
    active: { label: student.pointerCapable ? "IN TABBLAD" : "ACTIEF · TOUCH", Icon: Eye, title: student.pointerCapable ? "Tab zichtbaar, venster actief en cursor binnen." : "Tab zichtbaar en actief op een apparaat zonder cursor." },
    "other-tab": { label: "ANDER TABBLAD", Icon: EyeOff, title: "Het CodeQuest-tabblad is verborgen of het venster heeft geen focus." },
    "cursor-out": { label: "CURSOR BUITEN", Icon: MousePointer2, title: "CodeQuest is zichtbaar, maar de cursor staat buiten het tabblad." },
    idle: { label: "60S STIL", Icon: Clock3, title: "Het tabblad is zichtbaar, maar er was minimaal 60 seconden geen invoer." },
    offline: { label: "OFFLINE", Icon: WifiOff, title: "Er kwam de afgelopen 35 seconden geen heartbeat binnen." },
  } as const;
  const current = states[student.presenceState] ?? states.offline;
  return (
    <span className={`presence-badge presence-${student.presenceState}`} title={current.title}>
      <i /><current.Icon /><b>{current.label}</b>
    </span>
  );
}

type LiveCodeResponse = {
  student: { id: string; nickname: string };
  snapshot: null | { taskId: string; lessonId: number; code: string; updatedAt: string };
};

function LiveCodeViewer({ student, onClose }: { student: AdminState["students"][number]; onClose: () => void }) {
  const [data, setData] = useState<LiveCodeResponse | null>(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [accessMessage, setAccessMessage] = useState("");

  const refreshCode = useCallback(async () => {
    try {
      setData(await api<LiveCodeResponse>(`admin/live-code?studentId=${encodeURIComponent(student.id)}`));
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Code ophalen lukte niet.");
    }
  }, [student.id]);

  useEffect(() => {
    void refreshCode();
    const timer = window.setInterval(() => void refreshCode(), 2000);
    return () => window.clearInterval(timer);
  }, [refreshCode]);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const snapshot = data?.snapshot ?? null;
  const taskTitle = snapshot
    ? lessons.flatMap((lesson) => lesson.tasks).find((task) => task.id === snapshot.taskId)?.title ?? snapshot.taskId
    : "Nog geen opdracht geopend";

  async function copyCode() {
    if (!snapshot) return;
    try {
      await navigator.clipboard.writeText(snapshot.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Kopiëren lukte niet.");
    }
  }

  async function resetAccessCode() {
    if (!window.confirm(`Persoonlijke code van ${student.nickname} resetten? De leerling kiest bij de volgende login zelf een nieuwe code.`)) return;
    try {
      await api("admin/reset-access-code", { method: "POST", body: JSON.stringify({ studentId: student.id }) });
      setAccessMessage("Code gereset · leerling kiest bij de volgende login een nieuwe");
      setError("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Code resetten lukte niet.");
    }
  }

  return (
    <div className="live-code-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="live-code-dialog" role="dialog" aria-modal="true" aria-labelledby="live-code-title">
        <header>
          <div><span className="live-code-kicker"><i /> LIVE · ELKE 2 SEC</span><h2 id="live-code-title">Code van {student.nickname}</h2></div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Live code sluiten"><X /></button>
        </header>
        {error && <p className="form-error" role="alert"><X size={16} />{error}</p>}
        {snapshot ? (
          <>
            <div className="live-code-meta">
              <span><b>MISSIE {snapshot.lessonId}</b>{taskTitle}</span>
              <span>Bijgewerkt {new Date(`${snapshot.updatedAt.replace(" ", "T")}Z`).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>
              <button type="button" onClick={() => void resetAccessCode()}><KeyRound />Reset inlogcode</button>
              <button type="button" onClick={() => void copyCode()}><Copy />{copied ? "Gekopieerd" : "Kopieer code"}</button>
            </div>
            {accessMessage && <p className="live-code-notice">{accessMessage}</p>}
            <pre className="live-code-source" aria-label={`Actuele Python-code van ${student.nickname}`}><code>{snapshot.code || "# Leeg codevenster"}</code></pre>
          </>
        ) : (
          <><div className="live-code-reset-row"><button type="button" onClick={() => void resetAccessCode()}><KeyRound />Reset persoonlijke code</button></div>{accessMessage && <p className="live-code-notice">{accessMessage}</p>}<div className="live-code-empty"><Code2 /><h3>Nog geen code ontvangen</h3><p>Zodra {student.nickname} een opdracht opent, verschijnt de actuele code hier automatisch.</p></div></>
        )}
      </section>
    </div>
  );
}

function ClassManager({ state, refresh }: { state: AdminState; refresh: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const result = await api<{ class: AdminState["class"]; token: string }>("admin/classes", {
        method: "POST",
        body: JSON.stringify({ name, code }),
      });
      setSessionToken(result.token);
      setName("");
      setCode("");
      setMessage(`Klas ${result.class.code} is gemaakt en geopend.`);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Klas maken lukte niet.");
    } finally {
      setBusy(false);
    }
  }

  async function selectClass(classId: string) {
    if (classId === state.class.id) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const result = await api<{ token: string }>("admin/class/switch", { method: "POST", body: JSON.stringify({ classId }) });
      setSessionToken(result.token);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Wisselen van klas lukte niet.");
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(state.class.code);
      setMessage(`Klascode ${state.class.code} is gekopieerd.`);
      setError("");
    } catch {
      setError("Kopiëren lukte niet; selecteer de code handmatig.");
    }
  }

  async function toggleClosed() {
    const willClose = !state.class.isClosed;
    if (willClose && !window.confirm(`Klas ${state.class.name} sluiten? Leerlingen worden uitgelogd en kunnen pas verder nadat je de klas heropent.`)) return;
    setBusy(true);
    setMessage("");
    setError("");
    try {
      await api("admin/class/status", { method: "POST", body: JSON.stringify({ closed: willClose }) });
      setMessage(willClose ? `Klas ${state.class.code} is gesloten. De voortgang blijft bewaard.` : `Klas ${state.class.code} is weer geopend.`);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "De klasstatus wijzigen lukte niet.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="class-manager">
      <div className="class-manager-head">
        <div className="section-title"><KeyRound /><div><span>KLASSEN & CODES</span><h2>Werk met meerdere crews</h2></div></div>
        <div className="class-manager-actions">
          <button className="copy-code" type="button" onClick={() => void copyCode()}><Copy />Kopieer {state.class.code}</button>
          <button className={`class-status-button ${state.class.isClosed ? "reopen" : "close"}`} type="button" onClick={() => void toggleClosed()} disabled={busy}>
            {state.class.isClosed ? <Unlock /> : <Lock />}{state.class.isClosed ? "Heropen klas" : "Sluit klas"}
          </button>
        </div>
      </div>
      <p className="class-manager-note">Elke klascode heeft eigen leerlingen, voortgang en lesfasering. Een gesloten klas bewaart alles, maar blokkeert alle leerlingtoegang.</p>
      <div className="class-switcher" aria-label="Beschikbare klassen">
        {state.classes.map((item) => (
          <button type="button" key={item.id} className={`${item.id === state.class.id ? "current" : ""} ${item.isClosed ? "closed" : ""}`} onClick={() => void selectClass(item.id)} disabled={busy}>
            <span>{item.code}</span><strong>{item.name}</strong><small>{item.isClosed ? "KLAS GESLOTEN" : `open t/m missie ${item.openLesson}`}</small>
          </button>
        ))}
      </div>
      <form className="class-create" onSubmit={create}>
        <label>Klasnaam<input value={name} onChange={(event) => setName(event.target.value)} placeholder="bijv. Techclub woensdag" maxLength={40} /></label>
        <label>Klascode <span>(optioneel)</span><input value={code} onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))} placeholder="automatisch genereren" maxLength={16} /></label>
        <button className="primary-button" disabled={busy || name.trim().length < 2}><Plus />{busy ? "Even wachten…" : "Maak & open klas"}</button>
      </form>
      {(message || error) && <p className={error ? "manager-message error" : "manager-message"}>{error || message}</p>}
    </section>
  );
}

function AdminApp({ state, refresh, logout }: { state: AdminState; refresh: () => Promise<void>; logout: () => Promise<void> }) {
  const [busyLesson, setBusyLesson] = useState<number | null>(null);
  const [codeStudent, setCodeStudent] = useState<AdminState["students"][number] | null>(null);
  const openQuestions = state.questions.filter((question) => question.status === "open").length;
  const average = state.students.length ? Math.round(state.students.reduce((sum, item) => sum + item.completed, 0) / state.students.length) : 0;
  const activeStudents = state.students.filter((student) => student.presenceState === "active").length;

  async function unlock(openLesson: number) {
    setBusyLesson(openLesson);
    try {
      await api("admin/unlock", { method: "POST", body: JSON.stringify({ openLesson }) });
      await refresh();
    } finally {
      setBusyLesson(null);
    }
  }

  return (
    <div className="admin-shell">
      <header className="topbar admin-topbar">
        <Brand />
        <div className="admin-breadcrumb">DOCENTENPANEEL <ChevronRight /> {state.class.name}</div>
        <div className={`class-code ${state.class.isClosed ? "closed" : ""}`}><small>{state.class.isClosed ? "GESLOTEN" : "KLASCODE"}</small><strong>{state.class.code}</strong></div>
        <button className="icon-button" onClick={logout} aria-label="Uitloggen"><LogOut /></button>
      </header>
      <main className="admin-main">
        <section className="admin-heading">
          <div><p className="eyebrow"><span /> LIVE OVERZICHT</p><h1>Mission Control</h1><p>Geef missies vrij, spot vastlopers en stuur gerichte hints.</p></div>
          <button className="ghost-button" onClick={() => void refresh()}><RefreshCw /> Vernieuwen</button>
        </section>

        <section className="phase-control">
          <div className="section-title"><Unlock /><div><span>FASEBEHEER</span><h2>Open tot en met welke missie?</h2></div></div>
          <div className="phase-track">
            {lessons.map((lesson) => {
              const active = lesson.id <= state.class.openLesson;
              const lastOpen = lesson.id === state.class.openLesson;
              return (
                <button key={lesson.id} className={`${active ? "open" : "locked"} ${lastOpen ? "last-open" : ""}`} onClick={() => void unlock(lesson.id)} disabled={busyLesson !== null} title={`Open de lessen tot en met missie ${lesson.id}`}>
                  <span>{busyLesson === lesson.id ? <RefreshCw className="spin" /> : active ? <Check /> : <Lock />}</span>
                  <div><small>{lesson.code}</small><strong>{lesson.title}</strong></div>
                  <b>{lastOpen ? "LAATSTE OPEN" : active ? "OPEN" : "OPEN T/M"}</b>
                </button>
              );
            })}
          </div>
          <p className="phase-note">Klik op een eerdere missie om alle missies daarna weer te vergrendelen. Les 1 blijft altijd open en opgeslagen voortgang blijft bewaard.</p>
        </section>

        <ClassManager state={state} refresh={refresh} />

        <section className="admin-stats">
          <article><span><Eye /></span><div><small>ACTIEF IN TAB</small><strong>{activeStudents}</strong><p>van {state.students.length} aangemeld</p></div></article>
          <article><span><Gauge /></span><div><small>GEMIDDELD</small><strong>{average}<em>/{CORE_TASK_COUNT}</em></strong><p>kerncheckpoints per leerling</p></div></article>
          <article className={openQuestions ? "attention" : ""}><span><MessageSquare /></span><div><small>HULPVRAGEN</small><strong>{openQuestions}</strong><p>wachten op antwoord</p></div></article>
        </section>

        <div className="admin-grid">
          <section className="crew-panel">
            <div className="panel-head"><div className="section-title"><Users /><div><span>VOORTGANG</span><h2>Crew monitor</h2></div></div><span>Live · elke 2 sec</span></div>
            <p className="presence-explainer"><Eye />Live momentopnames, geen logboek. “Cursor buiten” kan ook de browserbalk of een tweede scherm betekenen; code toont alleen de laatste versie.</p>
            <div className="crew-table" role="table" aria-label="Voortgang leerlingen">
              <div className="crew-row crew-header" role="row"><span>CODENAAM</span><span>CHECKPOINTS</span><span>TAB & CURSOR</span><span>LIVE CODE</span><span>POGINGEN</span><span>STATUS</span></div>
              {state.students.length === 0 ? <p className="empty-note">Nog niemand aangemeld. Deel klascode <strong>{state.class.code}</strong>.</p> : state.students.map((student) => (
                <div className="crew-row" role="row" key={student.id}>
                  <span className="crew-name"><i>{student.nickname.slice(0, 2).toUpperCase()}</i><b>{student.nickname}</b></span>
                  <span className="mini-progress"><i><b style={{ width: `${(student.completed / CORE_TASK_COUNT) * 100}%` }} /></i>{student.completed}/{CORE_TASK_COUNT}{student.bonusCompleted > 0 && <small>+{student.bonusCompleted}/{BONUS_TASK_COUNT} bonus</small>}</span>
                  <PresenceBadge student={student} />
                  <button className="live-code-button" type="button" onClick={() => setCodeStudent(student)}><Code2 />Bekijk</button>
                  <span>{student.attempts}</span>
                  <span className={student.completed === CORE_TASK_COUNT ? "status-done" : student.attempts > 4 && student.completed < 2 ? "status-help" : "status-active"}>{student.completed === CORE_TASK_COUNT ? "KLAAR" : student.attempts > 4 && student.completed < 2 ? "CHECK IN" : "BEZIG"}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="inbox-panel">
            <div className="panel-head"><div className="section-title"><MessageSquare /><div><span>INBOX</span><h2>Hulpvragen</h2></div></div><div className="panel-live-status"><span>Live · elke 2 sec</span>{openQuestions > 0 && <span className="inbox-badge">{openQuestions} nieuw</span>}</div></div>
            <div className="inbox-list">
              {state.questions.length === 0 ? <p className="empty-note">Nog geen vragen. Dat is óf heel goed, óf verdacht stil.</p> : state.questions.map((question) => <AnswerBox key={question.id} question={question} onAnswered={refresh} />)}
            </div>
          </section>
        </div>
      </main>
      {codeStudent && <LiveCodeViewer student={codeStudent} onClose={() => setCodeStudent(null)} />}
    </div>
  );
}

export default function App() {
  const [state, setState] = useState<AppState | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setState(await api<AppState>("state"));
    } catch {
      setState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      setState(await api<AppState>("state"));
    } catch (cause) {
      if (cause instanceof ApiError && cause.status === 423) {
        clearSessionToken();
        setState(null);
      }
      // Een korte netwerkstoring mag een actieve les niet uitloggen.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!state?.role) return;
    const updateWhenVisible = () => {
      if (document.visibilityState === "visible") void poll();
    };
    const timer = window.setInterval(updateWhenVisible, 2_000);
    document.addEventListener("visibilitychange", updateWhenVisible);
    window.addEventListener("focus", updateWhenVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", updateWhenVisible);
      window.removeEventListener("focus", updateWhenVisible);
    };
  }, [poll, state?.role]);

  const logout = useCallback(async () => {
    await api("logout", { method: "POST" }).catch(() => undefined);
    clearSessionToken();
    setState(null);
  }, []);

  const content = useMemo(() => {
    if (loading) return <div className="loading-screen"><Brand /><div className="loader-line"><span /></div><p>Mission Control verbinden…</p></div>;
    if (!state) return <LoginScreen onLogin={refresh} />;
    if (state.role === "student") return <StudentApp state={state} refresh={refresh} logout={logout} />;
    return <AdminApp state={state} refresh={refresh} logout={logout} />;
  }, [loading, logout, refresh, state]);

  return content;
}
